/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 27-07-2026
 *
 * Root-service van de feedbackbalk (golf f1); sinds golf 3c de eigenaar van de vier
 * balk-toggles. Een toggle is hier: guard, routebeslissing (een volger vraagt het de leider) en
 * uitvoering (documentstate in `DlModel` zetten, selectie wissen waar dat hoort). Wat overblijft
 * voor de samenwerk-baas is puur transport en log, via twee smalle callbacks: `vraagToggle` zet het
 * verzoek van een volger op de juiste samenwerklijn, `meldToggle` doet ná een uitvoering het
 * actielog, de undo/save-staart en de leiderbroadcast.
 *
 * Daarnaast de init-lock tijdens het tekenen van een model en de push-ingangen
 * `reset`/`sluitPopupteksten`, die het modelcanvas aanroept (in golf f1 de balk-brug naar
 * AngularJS). Het normfout-overzicht leeft sinds golf 3b in de `NormService`, de feedbackselectie in
 * de `FeedbackSelectieService`.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {DlModel} from '../app/model/dl-model';
import {BouwsuggestieEngineService} from '../app/engines/bouwsuggestie-engine.service';
import {NormService} from '../app/engines/norm.service';
import {FeedbackSelectieService} from './feedback-selectie.service';
import {ProjectVlaggenService} from './project-vlaggen.service';
import {SamenwerkingService} from './samenwerking.service';
import {SimulatieStatusService} from './simulatie-status.service';

/**
 * De vier toggle-soorten van de balk. `noodzakelijk` en `gewenst` zijn de twee
 * bouwsuggestiesoorten; de namen zijn dezelfde als in het `bsToon`-samenwerkbericht.
 */
export type FeedbackToggleSoort = 'sim' | 'norm' | 'noodzakelijk' | 'gewenst';

/**
 * De ketens richting de samenwerk-baas: transport en log rond de toggles.
 */
export interface FeedbackbalkActies {
    /**
     * Een volger wil een toggle: zet het verzoek op de juiste samenwerklijn (norm → het
     * `normToon`-samenwerkbericht, bs → het `bsToon`-samenwerkbericht, sim → het
     * `simfeedbackactie`-simulatiebericht). De leider voert uit en broadcast de nieuwe stand
     * terug.
     */
    vraagToggle: (soort: FeedbackToggleSoort, toon: boolean) => void;

    /**
     * De staart ná een uitgevoerde toggle (leider/lokaal): actielog, undo-punt, save en de
     * leiderbroadcast naar de volgers. `volger` is gevuld als het oorspronkelijke verzoek van een
     * volger kwam, voor het actielog.
     */
    meldToggle: (soort: FeedbackToggleSoort, toon: boolean, volger?: string) => void;
}

/**
 * De push-ingangen die het eiland aanbiedt, zodat het modelcanvas de popups kan sturen.
 */
export interface FeedbackbalkWeergave {
    /** Wis de popupteksten en de onthouden tellerstanden (bij laden/herladen). */
    reset: () => void;
    /** Wis alleen de popupteksten (bij een klik elders of een canvasactie). */
    sluitPopupteksten: () => void;
}

@Injectable({providedIn: 'root'})
export class FeedbackbalkService {

    /**
     * Het modeldocument: de vier toon-vlaggen en de `fullReadonly`-guard.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * Draait de simulatie? Sim toggle't alleen tijdens, de andere drie alleen buiten een simulatie.
     */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De norm-service: guard van de norm-toggle (alleen zinvol bij fouten).
     */
    private readonly normService = inject(NormService);

    /**
     * De bouwsuggestie-engine: de totalen voor de tellers en de bs-toggle-guards.
     */
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);

    /**
     * De projectvlaggen: welke feedbacksoorten dit project überhaupt heeft.
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    /**
     * De samenwerk-state: een volger voert een toggle niet zelf uit maar vraagt hem de leider.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * De feedbackselectie: de sim-toggle en het uitzetten van norm wissen de selectie.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * Of het modelcanvas net een document tekent (tot kort daarna); dan onderdrukken we de
     * "nieuwe dingen"-ballonnen.
     */
    private readonly _initLock = signal(false);

    /**
     * Of het modelcanvas net een document tekent.
     */
    readonly initLock = this._initLock.asReadonly();

    /**
     * Het aantal noodzakelijke bouwsuggesties over het hele model (0 als het project ze niet heeft).
     */
    readonly noodzakelijkTelling = computed(() =>
        this.projectVlaggen.bouwsuggestiesNoodzakelijk() ? this.bouwsuggestieEngine.totalen().noodzakelijk : 0);

    /**
     * Het aantal gewenste bouwsuggesties over het hele model (0 als het project ze niet heeft).
     */
    readonly gewenstTelling = computed(() =>
        this.projectVlaggen.bouwsuggestiesGewenst() ? this.bouwsuggestieEngine.totalen().gewenst : 0);

    /**
     * Staat de balk op slot? Naast de readonly-stand telt de replay apart mee: die zet zijn
     * blokkade pas ná het ophalen van de snapshotlijst, en in dat venster mag een klik geen
     * logregel, geen samenwerkbericht en geen normronde meer opleveren.
     */
    private get opSlot(): boolean {
        return this.dlModel.fullReadonly || this.dlModel.inReplay();
    }

    /**
     * De door de samenwerk-baas geregistreerde transport- en logketens.
     */
    private acties: FeedbackbalkActies | null = null;

    /**
     * De door het eiland geregistreerde push-ingangen.
     */
    private weergave: FeedbackbalkWeergave | null = null;

    /**
     * De samenwerk-baas registreert de transport- en logketens (tot golf 4f deed AngularJS dat).
     */
    initialiseer(acties: FeedbackbalkActies): void {
        this.acties = acties;
    }

    /**
     * Het eiland registreert zijn push-ingangen.
     */
    registreerWeergave(weergave: FeedbackbalkWeergave): void {
        this.weergave = weergave;
    }

    /**
     * Het modelcanvas meldt of het net een document tekent.
     */
    zetInitLock(initLock: boolean): void {
        this._initLock.set(!!initLock);
    }

    /* ******** Richting het eiland ******** */

    /**
     * Wist de popupteksten en de onthouden tellerstanden, zodat de eerste waarden na laden geen
     * melding opleveren.
     */
    reset(): void {
        this.weergave?.reset();
    }

    /**
     * Wist alleen de popupteksten.
     */
    sluitPopupteksten(): void {
        this.weergave?.sluitPopupteksten();
    }

    /* ******** De toggles ******** */

    /**
     * Toggle de simulatiefeedback: alleen tijdens een simulatie en niet als de balk op slot staat.
     */
    toggleSim(): void {
        if (this.opSlot || !this.simulatieStatus.simuleren()) {
            return;
        }
        this.zetToggle('sim', !this.dlModel.simToon);
    }

    /**
     * Toggle de normfoutfeedback: alleen buiten een simulatie, niet als de balk op slot staat, en
     * alleen zinvol als er fouten zijn.
     */
    toggleNorm(): void {
        if (this.opSlot || this.simulatieStatus.simuleren() || !this.normService.aantalFouten()) {
            return;
        }
        this.zetToggle('norm', !this.dlModel.normToon);
    }

    /**
     * Toggle de noodzakelijke bouwsuggesties: alleen buiten een simulatie, niet als de balk op slot
     * staat, en alleen zinvol als er suggesties zijn.
     */
    toggleBsNoodzakelijk(): void {
        if (this.opSlot || this.simulatieStatus.simuleren() || !this.noodzakelijkTelling()) {
            return;
        }
        this.zetToggle('noodzakelijk', !this.dlModel.bsToonNoodzakelijk);
    }

    /**
     * Toggle de gewenste bouwsuggesties; zelfde regels als de noodzakelijke.
     */
    toggleBsGewenst(): void {
        if (this.opSlot || this.simulatieStatus.simuleren() || !this.gewenstTelling()) {
            return;
        }
        this.zetToggle('gewenst', !this.dlModel.bsToonGewenst);
    }

    /**
     * Voert een toggle uit: de state zetten en de staart (log/undo/save/broadcast) aan de
     * samenwerk-baas melden. Ook de ingang voor zijn dispatcher wanneer de leider het verzoek van een
     * volger uitvoert — dan gaat de volger-id mee voor het actielog. Bewust zonder de klik-guards: die
     * horen bij de klik, niet bij de uitvoering.
     */
    voerToggleUit(soort: FeedbackToggleSoort, toon: boolean, volger?: string): void {
        this.zetToggleState(soort, toon);
        this.acties?.meldToggle(soort, toon, volger);
    }

    /**
     * Neemt een door de leider gebroadcaste toggle-stand over (volger, via de
     * `normToon`-/`bsToon`-dispatchercases): alleen de state — loggen, undo en saven doet de
     * leider.
     */
    neemToggleOver(soort: FeedbackToggleSoort, toon: boolean): void {
        this.zetToggleState(soort, toon);
    }

    /**
     * Zet de documentstate van een toggle en wist de selectie waar dat hoort.
     */
    private zetToggleState(soort: FeedbackToggleSoort, toon: boolean): void {
        switch (soort) {
            case 'sim':
                //wis eerst: een openstaande selectie hoort niet door een toon-wissel heen te blijven
                this.feedbackSelectie.wisSelectie();
                this.dlModel.simToon = toon;
                break;
            case 'norm':
                this.dlModel.normToon = toon;
                if (!toon) {
                    //bij uitzetten verdwijnt de selectie met de weergave mee
                    this.feedbackSelectie.wisSelectie();
                }
                break;
            case 'noodzakelijk':
                this.dlModel.bsToonNoodzakelijk = toon;
                break;
            case 'gewenst':
                this.dlModel.bsToonGewenst = toon;
                break;
        }
    }

    /**
     * De routebeslissing van een geklikte toggle: een volger vraagt het de leider (die uitvoert en
     * de nieuwe stand terugbroadcast), ieder ander voert direct uit.
     */
    private zetToggle(soort: FeedbackToggleSoort, toon: boolean): void {
        if (this.samenwerking.isVolger()) {
            this.acties?.vraagToggle(soort, toon);
        } else {
            this.voerToggleUit(soort, toon);
        }
    }
}
